import type { Character, CharactersSnapshot } from "@/features/characters/types";
import {
  getStorageMode,
  type ActiveStorageEngine,
} from "@/features/settings/storage/preferences";
import { sortCharacters } from "@/features/characters/character-utils";
import { indexedDbStorage } from "@/features/characters/storage/indexeddb";
import { localStorageAdapter } from "@/features/characters/storage/localstorage";
import type { CharactersStorageAdapter } from "@/features/characters/storage/storage-adapter";

async function resolveAdapter(): Promise<{
  adapter: CharactersStorageAdapter;
  engine: ActiveStorageEngine;
}> {
  const mode = getStorageMode();

  if (mode !== "localstorage") {
    try {
      await indexedDbStorage.ping();
      return { adapter: indexedDbStorage, engine: "indexeddb" };
    } catch {
      if (mode === "indexeddb") {
        console.warn("IndexedDB indisponível, usando localStorage como fallback.");
      }
    }
  }

  await localStorageAdapter.ping();
  return { adapter: localStorageAdapter, engine: "localstorage" };
}

export async function listCharacters(): Promise<CharactersSnapshot> {
  const { adapter, engine } = await resolveAdapter();
  const items = sortCharacters(await adapter.list());
  return { items, engine };
}

export async function getCharacter(id: string): Promise<{
  item: Character | null;
  engine: ActiveStorageEngine;
}> {
  const { adapter, engine } = await resolveAdapter();
  const item = await adapter.get(id);
  return { item, engine };
}

export async function saveCharacter(character: Character): Promise<ActiveStorageEngine> {
  const { adapter, engine } = await resolveAdapter();
  await adapter.save(character);
  return engine;
}

export async function removeCharacter(id: string): Promise<ActiveStorageEngine> {
  const { adapter, engine } = await resolveAdapter();
  await adapter.delete(id);
  return engine;
}

export async function clearCharacters(): Promise<ActiveStorageEngine> {
  const { adapter, engine } = await resolveAdapter();
  await adapter.clear();
  return engine;
}
